import { el, clear } from "../components/dom.js";
import { persistedState } from "../utils/list-prefs.js";
import { bulkSelect } from "../components/bulk-select.js";
import { randomPickButton } from "../components/random-pick.js";
import { BOOK_OWNERSHIP_STATUSES } from "../components/status-badge.js";
import { filterBar } from "../components/filter-bar.js";
import { groupToggle } from "../components/group-toggle.js";
import { parseFilters, matchesAllFilters } from "../utils/filters.js";
import { listComics } from "../db/comics.js";
import { COMIC_FORMATS, comicFormatLabel, compareIssueNumbers } from "../utils/comics.js";
import { listGroups, categoryGroups } from "../db/groups.js";
import { listAllMedia } from "../db/media.js";
import { getRankingState } from "../db/ranking.js";
import { enableRowDrag } from "../utils/drag-drop.js";
import { sortedEntries } from "../utils/group-sort.js";
import { handleRowDrop } from "./group-dnd.js";
import { renderCategoryEntries } from "../components/mag-gallery.js";
import { viewToggle } from "../components/view-toggle.js";
import { getListView, setListView } from "../utils/view-mode.js";
import { navigate, navigateBack, getQueryParams } from "../main.js";
import { saveScroll, consumeScroll } from "../utils/scroll.js";

// The Comics category list — the movies list with an "Edition" facet (single
// issues / trades / hardcovers / omnibuses) instead of genres, and a Series sort
// that keeps a run's issues together in number order.

const SCROLL_KEY = "comics-list";
const EMPTY_IDS = new Set(); // shared "collapse nothing" set for ungrouped view

const SORTS = [
	{ id: "created_at", label: "Date added" },
	{ id: "title", label: "Title A–Z" },
	{ id: "series", label: "Series" },
	{ id: "release_year", label: "Release year" },
	{ id: "last_engaged_date", label: "Last read" },
	{ id: "rating", label: "Rating" },
	{ id: "battle_rank", label: "Battle rank" },
];

const state = persistedState("comics-list", {
	sort: "created_at",
	// "gallery" (masonry tiles) or "list" (glass rows) — mirrors the device-wide
	// setting in utils/view-mode.js; re-read every time the screen renders.
	view: "gallery",
	edition: "", // "" = every edition type (issue / tpb / hc / omnibus / other)
	// false = collections show as group cards (members collapsed); true = groups
	// are dissolved and every member shows as its own item in the list.
	expandGroups: false,
});

export async function renderComicsList(root) {
	const header = el(
		"header.app-header",
		{},
		el(
			"button.app-header__back",
			{ type: "button", onclick: () => navigateBack("/") },
			"‹",
		),
		el("h1", {}, "Comics"),
		el(
			"button.app-header__icon",
			{ type: "button", title: "VS Battle", onclick: () => navigate("/comics/vs") },
			"VS",
		),
		randomPickButton("comic"),
		el(
			"button.app-header__action",
			{ type: "button", onclick: () => navigate("/comics/add") },
			"+ Add",
		),
	);

	const sortSelect = el(
		"select.toolbar__sort",
		{
			value: state.sort,
			onchange: (e) => {
				state.sort = e.currentTarget.value;
				renderList();
			},
		},
		...SORTS.map((s) =>
			el("option", { value: s.id, selected: state.sort === s.id }, s.label),
		),
	);

	// Edition facet — only the edition types actually in the library.
	const editionSelect = el("select.toolbar__sort", {
		onchange: (e) => {
			state.edition = e.currentTarget.value;
			renderList();
		},
	});

	const filter = filterBar({
		mediaType: "comic",
		ownershipStatuses: BOOK_OWNERSHIP_STATUSES,
		initial: parseFilters(getQueryParams()),
		onChange: () => renderList(),
	});

	// Grouped ⇄ ungrouped view toggle (hidden when there are no groups).
	const groupBtn = groupToggle({
		expanded: state.expandGroups,
		onChange: (expanded) => {
			state.expandGroups = expanded;
			renderList();
		},
	});

	// Gallery ⇄ list layout toggle — one device-wide setting shared by every
	// category list (utils/view-mode.js).
	state.view = getListView();
	const viewBtn = viewToggle({
		mode: state.view,
		onChange: (mode) => {
			state.view = setListView(mode);
			renderList();
		},
	});

	// Select several items → status / ownership / group / delete (roadmap #17).
	const bulk = bulkSelect({ mediaType: "comic" });

	const toolbar = el(
		"div.toolbar",
		{},
		el(
			"div.toolbar__row",
			{},
			el("label.toolbar__sort-wrap", {}, el("span", {}, "Sort: "), sortSelect),
			el("label.toolbar__sort-wrap", {}, el("span", {}, "Edition: "), editionSelect),
			groupBtn,
			viewBtn,
			bulk.button,
			filter.button,
		),
		filter.panel,
		filter.chips,
	);

	// Entries to the creator view (follow a writer/artist, see their runs) and to
	// series subscriptions (release notices).
	const navLink = (label, route) =>
		el(
			"button",
			{
				type: "button",
				onclick: () => navigate(route),
				style: {
					flex: "1", minWidth: "0", display: "flex", alignItems: "center", justifyContent: "space-between",
					gap: "8px", padding: "10px 12px", cursor: "pointer",
					borderRadius: "12px", border: "1px solid rgba(255,255,255,.14)", background: "rgba(31,191,95,.14)",
					color: "#f5f5f7", fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11.5px",
					letterSpacing: ".04em", textTransform: "uppercase",
				},
			},
			el("span", {}, label),
			el("span", { "aria-hidden": "true" }, "›"),
		);
	const comicLinks = el(
		"div",
		{ style: { display: "flex", gap: "8px", margin: "8px 16px 0" } },
		navLink("★ Creators", "/comics/creators"),
		navLink("🔔 Subscriptions", "/comics/subscriptions"),
	);

	const listContainer = el("div.games-list", {});
	bulk.attach(listContainer);
	const view = el("div.view", {}, header, comicLinks, toolbar, listContainer);
	root.append(view);

	let allComics = [];
	let rankIndex = new Map();
	let groupCards = [];
	let collapsedIds = new Set();

	function refreshEditionOptions() {
		const present = new Set(allComics.map((c) => c.format).filter(Boolean));
		// Drop a stale selection that no longer exists in the library.
		if (state.edition && !present.has(state.edition)) state.edition = "";
		clear(editionSelect);
		editionSelect.append(
			el("option", { value: "", selected: state.edition === "" }, "All"),
			...COMIC_FORMATS.filter((f) => present.has(f)).map((f) =>
				el("option", { value: f, selected: state.edition === f }, comicFormatLabel(f)),
			),
		);
	}

	function renderList() {
		clear(listContainer);

		const openGroup = (group) => {
			saveScroll(SCROLL_KEY);
			navigate(`/groups/${group.id}`);
		};
		const openComic = (comic) => {
			saveScroll(SCROLL_KEY);
			navigate(`/comics/${comic.id}`);
		};

		// The toggle is only meaningful when the category actually has groups.
		groupBtn.hidden = groupCards.length === 0;

		// Ungrouped view: dissolve the groups — show no group cards and collapse
		// nothing, so every member surfaces as its own item.
		const activeGroups = state.expandGroups ? [] : groupCards;
		const activeCollapsed = state.expandGroups ? EMPTY_IDS : collapsedIds;

		const filters = filter.getFilters();
		const filteredItems = allComics.filter(
			(c) =>
				!activeCollapsed.has(c.id) &&
				matchesAllFilters(c, filters) &&
				(!state.edition || c.format === state.edition),
		);
		// Groups sort inline among the comics by the active sort (each group ranks
		// as its leading member).
		const entries = sortedEntries({
			items: filteredItems,
			groupCards: activeGroups,
			sort: state.sort,
			rankIndex,
			sortFn: sortComics,
		});

		// Gallery tiles or glass rows (the toolbar toggle); groups render inline in
		// both, and every node carries data-drag-id/kind so drag-to-group works.
		renderCategoryEntries(listContainer, entries, {
			mediaType: "comic",
			view: state.view,
			count: filteredItems.length,
			onOpenItem: openComic,
			onOpenGroup: openGroup,
			empty: () => emptyState(allComics.length === 0, !!state.edition),
		});

		const savedY = consumeScroll(SCROLL_KEY);
		if (savedY != null) {
			requestAnimationFrame(() =>
				requestAnimationFrame(() => window.scrollTo(0, savedY)),
			);
		}
	}

	const [comics, ranking, groups, allMedia] = await Promise.all([
		listComics(),
		getRankingState("comic"),
		listGroups(),
		listAllMedia(),
	]);
	allComics = comics;
	rankIndex = new Map(ranking.order.map((id, i) => [id, i]));
	({ cards: groupCards, collapsedIds } = categoryGroups(groups, allMedia, "comic"));
	refreshEditionOptions();

	async function refreshGroups() {
		const fresh = await listGroups();
		({ cards: groupCards, collapsedIds } = categoryGroups(fresh, allMedia, "comic"));
		renderList();
	}
	enableRowDrag(listContainer, {
		onDrop: (payload) => handleRowDrop(payload, { allMedia, refresh: refreshGroups }),
	});

	renderList();
}

// A run's issues together, in issue order; collections (no series) by title.
function seriesSortKey(c) {
	return c.series?.name ? `${c.series.name} ${c.series.year_began ?? ""}` : c.title ?? "";
}

function sortComics(comics, sort, rankIndex) {
	const copy = comics.slice();
	switch (sort) {
		case "series":
			return copy.sort(
				(a, b) =>
					seriesSortKey(a).localeCompare(seriesSortKey(b)) ||
					compareIssueNumbers(a.number ?? "", b.number ?? ""),
			);
		case "title":
			return copy.sort((a, b) => a.title.localeCompare(b.title));
		case "release_year":
			return copy.sort(
				(a, b) => (b.release_year ?? 0) - (a.release_year ?? 0),
			);
		case "last_engaged_date":
			return copy.sort((a, b) =>
				(b.last_engaged_date ?? "").localeCompare(a.last_engaged_date ?? ""),
			);
		case "rating":
			return copy.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
		case "battle_rank":
			return copy.sort((a, b) => {
				const ra = rankIndex.has(a.id) ? rankIndex.get(a.id) : Infinity;
				const rb = rankIndex.has(b.id) ? rankIndex.get(b.id) : Infinity;
				if (ra !== rb) return ra - rb;
				return a.title.localeCompare(b.title);
			});
		case "created_at":
		default:
			return copy.sort((a, b) =>
				(b.created_at ?? "").localeCompare(a.created_at ?? ""),
			);
	}
}

function emptyState(noneAtAll, editionActive) {
	return el(
		"div.empty",
		{},
		el(
			"p",
			{},
			noneAtAll
				? "No comics yet. Tap + Add to find an issue or a collected edition."
				: editionActive
					? "No comics match this edition + filters."
					: "No comics match these filters.",
		),
	);
}
